import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, Vote, Users, BarChart3, Copy, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIONS = [
  'President',
  'Deputy President',
  'Vice Deputy President',
  'Ordinary Member - New Guinea Islands',
  'Ordinary Member - Momase',
  'Ordinary Member - Highlands',
  'Ordinary Member - Southern',
  'Ordinary Member - National Capital District'
];

const ElectionManager = () => {
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedElection, setSelectedElection] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  
  const [newElection, setNewElection] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    positions: POSITIONS
  });

  const [newCandidate, setNewCandidate] = useState({
    name: '',
    position_applying: '',
    bio: '',
    photo_url: ''
  });

  useEffect(() => {
    fetchElections();
  }, []);

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/elections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setElections(response.data);
    } catch (error) {
      console.error('Error fetching elections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateElection = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/elections`, newElection, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Election created successfully!');
      setShowCreateForm(false);
      setNewElection({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        positions: POSITIONS
      });
      fetchElections();
    } catch (error) {
      console.error('Error creating election:', error);
      toast.error('Failed to create election');
    }
  };

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    if (!selectedElection) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/elections/${selectedElection.id}/candidates`, newCandidate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidate added successfully!');
      setNewCandidate({ name: '', position_applying: '', bio: '', photo_url: '' });
      
      // Refresh election data
      const response = await axios.get(`${API}/elections/${selectedElection.id}`);
      setSelectedElection(response.data);
      fetchElections();
    } catch (error) {
      console.error('Error adding candidate:', error);
      toast.error('Failed to add candidate');
    }
  };

  const handleRemoveCandidate = async (candidateId) => {
    if (!selectedElection) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/elections/${selectedElection.id}/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidate removed');
      
      // Refresh election data
      const response = await axios.get(`${API}/elections/${selectedElection.id}`);
      setSelectedElection(response.data);
      fetchElections();
    } catch (error) {
      console.error('Error removing candidate:', error);
      toast.error('Failed to remove candidate');
    }
  };

  const handleUpdateStatus = async (electionId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/elections/${electionId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Election status updated to ${status}`);
      fetchElections();
      if (selectedElection?.id === electionId) {
        const response = await axios.get(`${API}/elections/${electionId}`);
        setSelectedElection(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteElection = async (electionId) => {
    if (!confirm('Are you sure? This will delete all votes for this election.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/elections/${electionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Election deleted');
      setSelectedElection(null);
      fetchElections();
    } catch (error) {
      console.error('Error deleting election:', error);
      toast.error('Failed to delete election');
    }
  };

  const handleViewResults = async (electionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/elections/${electionId}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to fetch results');
    }
  };

  const copyVotingLink = (electionId) => {
    const link = `${window.location.origin}/vote/${electionId}`;
    navigator.clipboard.writeText(link);
    toast.success('Voting link copied to clipboard!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Election Manager</h2>
          <p className="text-gray-600">Manage elections, candidates, and view results</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="mr-2" size={18} /> Create Election
        </Button>
      </div>

      {/* Create Election Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Election</CardTitle>
            <CardDescription>Set up a new election for the National Executive Committee</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateElection} className="space-y-4">
              <div>
                <Label>Election Title</Label>
                <Input
                  value={newElection.title}
                  onChange={(e) => setNewElection(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., 2024 National Executive Committee Election"
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newElection.description}
                  onChange={(e) => setNewElection(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the election"
                  required
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={newElection.start_date}
                    onChange={(e) => setNewElection(prev => ({ ...prev, start_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newElection.end_date}
                    onChange={(e) => setNewElection(prev => ({ ...prev, end_date: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-green-500 hover:bg-green-600">Create Election</Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Elections List */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Vote size={20} /> Elections
            </CardTitle>
          </CardHeader>
          <CardContent>
            {elections.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No elections created yet</p>
            ) : (
              <div className="space-y-3">
                {elections.map(election => (
                  <div
                    key={election.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedElection?.id === election.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                    }`}
                    onClick={() => setSelectedElection(election)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{election.title}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(election.start_date).toLocaleDateString()} - {new Date(election.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(election.status)}`}>
                        {election.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{election.candidates?.length || 0} candidates</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Election Details */}
        {selectedElection && (
          <Card>
            <CardHeader>
              <CardTitle>{selectedElection.title}</CardTitle>
              <CardDescription>{selectedElection.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyVotingLink(selectedElection.id)}
                >
                  <Copy size={16} className="mr-1" /> Copy Voting Link
                </Button>
                <a
                  href={`/vote/${selectedElection.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 text-sm border rounded-md hover:bg-gray-50"
                >
                  <ExternalLink size={16} className="mr-1" /> Preview
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewResults(selectedElection.id)}
                >
                  <BarChart3 size={16} className="mr-1" /> Results
                </Button>
              </div>

              {/* Status Controls */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="mb-2 block">Election Status</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={selectedElection.status === 'upcoming' ? 'default' : 'outline'}
                    onClick={() => handleUpdateStatus(selectedElection.id, 'upcoming')}
                  >
                    Upcoming
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedElection.status === 'active' ? 'default' : 'outline'}
                    className={selectedElection.status === 'active' ? 'bg-green-500' : ''}
                    onClick={() => handleUpdateStatus(selectedElection.id, 'active')}
                  >
                    Active
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedElection.status === 'closed' ? 'default' : 'outline'}
                    className={selectedElection.status === 'closed' ? 'bg-red-500' : ''}
                    onClick={() => handleUpdateStatus(selectedElection.id, 'closed')}
                  >
                    Closed
                  </Button>
                </div>
              </div>

              {/* Add Candidate Form */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users size={18} /> Add Candidate
                </h4>
                <form onSubmit={handleAddCandidate} className="space-y-3">
                  <div>
                    <Label>Candidate Name</Label>
                    <Input
                      value={newCandidate.name}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <select
                      value={newCandidate.position_applying}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, position_applying: e.target.value }))}
                      className="w-full h-10 px-3 border rounded-md"
                      required
                    >
                      <option value="">Select Position</option>
                      {POSITIONS.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Bio (Optional)</Label>
                    <Textarea
                      value={newCandidate.bio}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, bio: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Photo URL (Optional)</Label>
                    <Input
                      value={newCandidate.photo_url}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, photo_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
                    <Plus size={18} className="mr-1" /> Add Candidate
                  </Button>
                </form>
              </div>

              {/* Candidates List */}
              {selectedElection.candidates?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Candidates ({selectedElection.candidates.length})</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedElection.candidates.map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{candidate.name}</p>
                          <p className="text-xs text-gray-500">{candidate.position_applying}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleRemoveCandidate(candidate.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Election */}
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  className="w-full text-red-500 border-red-300 hover:bg-red-50"
                  onClick={() => handleDeleteElection(selectedElection.id)}
                >
                  <Trash2 size={18} className="mr-1" /> Delete Election
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results Modal */}
      {showResults && results && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold">Election Results</h3>
                  <p className="text-gray-500">{results.election_title}</p>
                </div>
                <Button variant="ghost" onClick={() => setShowResults(false)}>✕</Button>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <p className="text-blue-800 font-semibold">Total Voters: {results.total_voters}</p>
              </div>

              <div className="space-y-6">
                {Object.entries(results.results).map(([position, candidates]) => (
                  <div key={position}>
                    <h4 className="font-semibold text-gray-900 mb-3">{position}</h4>
                    <div className="space-y-2">
                      {Object.entries(candidates).map(([candidateId, data]) => (
                        <div key={candidateId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{data.candidate_name}</span>
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full font-semibold">
                            {data.votes} votes
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectionManager;
